<template>
  <HrPageLayout
    title="Employee Transfers"
    subtitle="Manage employee department and position transfers"
    icon="⇄"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'Employee Transfers' }]"
  >
    <template #header-actions>
      <button @click="refreshData" class="hr-btn hr-btn-secondary">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button @click="openNewTransferModal" class="hr-btn hr-btn-success">
        <i class="mdi mdi-plus"></i> New Transfer
      </button>
    </template>

    <div class="layout-content">
      <div class="page-wrapper">
        <!-- Stats Cards -->
        <div class="stats-grid">
          <!-- Total Transfers -->
          <div class="stat-card stat-primary">
            <div class="stat-icon">
              <span class="icon">⇄</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Total Transfers</div>
              <div class="stat-value">{{ summary.totalTransfers || 0 }}</div>
              <div v-if="summary.currentMonthTransfers" class="stat-subtext">
                {{ summary.currentMonthTransfers }} this month
              </div>
            </div>
          </div>

          <!-- Pending Transfers -->
          <div class="stat-card stat-warning">
            <div class="stat-icon">
              <span class="icon">⏱️</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Pending Approvals</div>
              <div class="stat-value">{{ summary.pendingTransfers || 0 }}</div>
              <div class="stat-subtext warning">Requires action</div>
            </div>
          </div>

          <!-- Pending Receipt (Approved, awaiting destination HR) -->
          <div class="stat-card stat-info">
            <div class="stat-icon">
              <span class="icon">📥</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Pending Receipt</div>
              <div class="stat-value">{{ summary.pendingReceiptCount || 0 }}</div>
              <div class="stat-subtext">Awaiting destination HR</div>
            </div>
          </div>

          <!-- Received (completed) -->
          <div class="stat-card stat-success">
            <div class="stat-icon">
              <span class="icon">✓</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Received</div>
              <div class="stat-value">{{ summary.receivedCount || 0 }}</div>
              <div class="stat-subtext success">Department updated</div>
            </div>
          </div>

          <!-- Rejected Transfers -->
          <div class="stat-card stat-danger">
            <div class="stat-icon">
              <span class="icon">✗</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Rejected</div>
              <div class="stat-value">{{ summary.rejectedTransfers || 0 }}</div>
              <div class="stat-subtext danger">Not processed</div>
            </div>
          </div>
        </div>

        <!-- Filters and Actions -->
        <div class="filters-card">
          <div class="filters-header">
            <h3 class="filters-title">Employee Transfers</h3>
          </div>
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">Status</label>
              <select v-model="filters.status" class="filter-select" @change="filterTransfers">
                <option value="">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="SourceApproved">Source Approved</option>
                <option value="Approved">Approved</option>
                <option value="Received">Received</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div class="filter-group">
              <label class="filter-label">From Date</label>
              <input type="date" v-model="filters.fromDate" class="filter-input" @change="filterTransfers">
            </div>

            <div class="filter-group">
              <label class="filter-label">To Date</label>
              <input type="date" v-model="filters.toDate" class="filter-input" @change="filterTransfers">
            </div>

            <div class="filter-group">
              <label class="filter-label">Search</label>
              <div class="search-input-container">
                <input
                  type="text"
                  v-model="filters.search"
                  class="filter-input search-input"
                  placeholder="Search employee or department..."
                  @input="debouncedSearch"
                >
                <span class="search-icon">🔍</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Transfers Table -->
        <div class="transfers-table-card">
          <!-- Loading indicator -->
          <div v-if="loading" class="loading-container">
            <div class="spinner"></div>
            <p>Loading transfer data...</p>
          </div>

          <!-- No data state -->
          <div v-else-if="!transfers.length" class="empty-container">
            <div class="empty-icon">📊</div>
            <h4>No transfer records found</h4>
            <p class="empty-text">Try adjusting your filters or create a new transfer request</p>
            <button @click="resetFilters" class="btn btn-outline">Reset Filters</button>
          </div>

          <!-- Transfers data table -->
          <div v-else class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>From Department</th>
                  <th>To Department</th>
                  <th>Transfer Date</th>
                  <th>Status</th>
                  <th>Initiated By</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transfer in transfers" :key="transfer.id" :class="getRowClass(transfer.status)">
                  <td>
                    <div class="employee-cell">
                      <div class="employee-avatar">
                        {{ getInitials(transfer.employeeName) }}
                      </div>
                      <div class="employee-info">
                        <div class="employee-name">{{ transfer.employeeName }}</div>
                        <div class="employee-id">{{ transfer.nationalId || 'ID not available' }}</div>
                      </div>
                    </div>
                  </td>
                  <td>{{ transfer.fromDepartmentName }}</td>
                  <td>{{ transfer.toDepartmentName }}</td>
                  <td>
                    <div class="date-cell">
                      {{ formatDate(transfer.transferDate) }}
                      <div v-if="transfer.effectiveDate && transfer.effectiveDate !== transfer.transferDate" class="effective-date">
                        Effective: {{ formatDate(transfer.effectiveDate) }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="status-badge" :class="getStatusBadgeClass(transfer.status)">
                      {{ transfer.status }}
                    </span>
                  </td>
                  <td>
                    <div class="initiator-cell">
                      {{ transfer.initiatedBy }}
                      <div class="initiator-date">{{ formatDate(transfer.createdAt, true) }}</div>
                    </div>
                  </td>
                  <td class="actions-cell">
                    <button @click="viewTransferDetails(transfer)" class="btn btn-icon btn-small btn-info" title="View Details">
                      <span class="icon">👁️</span>
                    </button>
                    <button
                      v-if="(transfer.status === 'Pending' && canApproveAsSource(transfer)) || (transfer.status === 'SourceApproved' && canApproveAsDestination(transfer))"
                      @click="approveTransfer(transfer)"
                      class="btn btn-icon btn-small btn-success"
                      :title="transfer.status === 'Pending' ? 'Confirm (Source ACO)' : 'Confirm (Destination ACO)'"
                    >
                      <span class="icon">✓</span>
                    </button>
                    <button
                      v-if="transfer.status === 'Pending' || transfer.status === 'SourceApproved'"
                      @click="rejectTransfer(transfer)"
                      class="btn btn-icon btn-small btn-danger"
                      title="Reject Transfer"
                    >
                      <span class="icon">✗</span>
                    </button>
                    <button
                      v-if="transfer.status === 'Approved' && canReceiveTransfer(transfer)"
                      @click="receiveTransfer(transfer)"
                      class="btn btn-icon btn-small btn-primary"
                      title="Receive employee (destination HR)"
                    >
                      <span class="icon">📥</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="transfers.length && pagination.totalPages > 1" class="pagination-container">
            <div class="pagination-info">
              Showing <span class="info-bold">{{ transfers.length }}</span> of
              <span class="info-bold">{{ pagination.totalRecords }}</span> records
            </div>
            <div class="pagination-controls">
              <button 
                class="pagination-btn" 
                :disabled="pagination.currentPage <= 1"
                @click="changePage(pagination.currentPage - 1)"
              >
                ←
              </button>
              
              <div class="pagination-pages">
                <button
                  v-for="page in getPageNumbers()"
                  :key="page"
                  class="page-btn"
                  :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                  @click="page !== '...' && changePage(page)"
                >
                  {{ page }}
                </button>
              </div>
              
              <button 
                class="pagination-btn"
                :disabled="pagination.currentPage >= pagination.totalPages"
                @click="changePage(pagination.currentPage + 1)"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <!-- New Transfer Modal -->
    <div v-if="showNewTransferModal" class="modal-overlay" @click="closeNewTransferModal">
      <div class="modal-container large" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Create Employee Transfer</h3>
          <button class="modal-close" @click="closeNewTransferModal">&times;</button>
        </div>
        <div class="modal-body">
          <!-- Employee Selection -->
          <div class="form-group">
            <label class="form-label required">Select Employee</label>
            <input type="text" v-model="employeeSearch" class="form-control mb-2" placeholder="Search employee by name, ID or employee number...">
            <select v-model="transferForm.employeeId" class="form-select" required @change="loadEmployeeData">
              <option value="">-- Select Employee --</option>
              <option v-for="employee in filteredEmployees" :key="employee.id" :value="employee.id">
                {{ employee.firstName }} {{ employee.lastName }} - {{ employee.departmentName }}
              </option>
            </select>
          </div>

          <!-- Employee details section -->
          <div v-if="selectedEmployee" class="employee-details-card">
            <div class="details-grid">
              <div class="details-column">
                <h4>Employee Details</h4>
                <div class="detail-item">
                  <strong>Name:</strong> {{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}
                </div>
                <div class="detail-item">
                  <strong>ID:</strong> {{ selectedEmployee.nationalId || 'N/A' }}
                </div>
                <div class="detail-item">
                  <strong>Position:</strong> {{ selectedEmployee.designation || 'N/A' }}
                </div>
              </div>
              <div class="details-column">
                <h4>Current Department</h4>
                <div class="detail-item">
                  <strong>Department:</strong> {{ selectedEmployee.departmentName || 'N/A' }}
                </div>
                <div class="detail-item">
                  <strong>Join Date:</strong> {{ formatDate(selectedEmployee.hireDate) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Target Department -->
          <div class="form-group">
            <label class="form-label required">Target Department</label>
            <select v-model="transferForm.toDepartmentId" class="form-select" required>
              <option value="">-- Select Department --</option>
              <option v-for="dept in departments" :key="dept.id" :value="dept.id"
                      :disabled="dept.id === selectedEmployee?.departmentID">
                {{ dept.departmentName }}
              </option>
            </select>
            <div v-if="selectedEmployee" class="form-hint">
              Current department: {{ selectedEmployee.departmentName }}
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label required">Transfer Date</label>
              <input type="date" v-model="transferForm.transferDate" class="form-input" required :min="today">
            </div>
            <div class="form-group">
              <label class="form-label">Effective Date</label>
              <input type="date" v-model="transferForm.effectiveDate" class="form-input" :min="transferForm.transferDate || today">
              <div class="form-hint">Date when transfer takes effect</div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group" style="width: 100%;">
              <label class="form-label">New Position (Optional)</label>
              <select v-model="transferForm.newPosition" class="form-select">
                <option value="">-- Keep Current Position --</option>
                <option v-for="pos in positions" :key="pos.id" :value="pos.positionName">
                  {{ pos.positionName }} ({{ pos.code }})
                </option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label class="form-label required">Reason for Transfer</label>
            <textarea v-model="transferForm.reason" class="form-textarea" rows="3" required placeholder="Explain reason for transfer"></textarea>
          </div>

          <div class="form-group">
            <label class="form-label">Additional Remarks (Optional)</label>
            <textarea v-model="transferForm.remarks" class="form-textarea" rows="2" placeholder="Additional comments or notes"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeNewTransferModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="submitTransferForm"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="btn-spinner"></span>
            {{ isSubmitting ? 'Creating...' : 'Create Transfer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Transfer Details Modal -->
    <div v-if="showDetailsModal && selectedTransfer" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal-container large" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Transfer Details</h3>
          <button class="modal-close" @click="closeDetailsModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="transfer-header">
            <div class="header-left">
              <h4>{{ selectedTransfer.employeeName }}</h4>
              <div class="transfer-id">ID: {{ selectedTransfer.nationalId || 'N/A' }}</div>
              <span class="status-badge" :class="getStatusBadgeClass(selectedTransfer.status)">
                {{ selectedTransfer.status }}
              </span>
            </div>
            <div class="header-right">
              <div class="transfer-date">Transfer Date: {{ formatDate(selectedTransfer.transferDate) }}</div>
              <div v-if="selectedTransfer.effectiveDate" class="effective-date">
                Effective Date: {{ formatDate(selectedTransfer.effectiveDate) }}
              </div>
              <div class="request-date">Requested: {{ formatDate(selectedTransfer.createdAt, true) }}</div>
            </div>
          </div>

          <div class="transfer-direction">
            <div class="department-box">
              <div class="department-label">From Department</div>
              <div class="department-name">{{ selectedTransfer.fromDepartmentName }}</div>
            </div>
            <div class="direction-arrow">→</div>
            <div class="department-box">
              <div class="department-label">To Department</div>
              <div class="department-name">{{ selectedTransfer.toDepartmentName }}</div>
            </div>
          </div>

          <div class="details-grid">
            <div class="details-card">
              <div class="card-header">
                <h5>Transfer Details</h5>
              </div>
              <div class="card-body">
                <div v-if="selectedTransfer.newPosition" class="detail-row">
                  <strong>New Position:</strong> {{ selectedTransfer.newPosition }}
                </div>
                <div class="detail-row">
                  <strong>Reason:</strong> {{ selectedTransfer.reason || 'No reason provided' }}
                </div>
                <div v-if="selectedTransfer.remarks" class="detail-row">
                  <strong>Remarks:</strong> {{ selectedTransfer.remarks }}
                </div>
              </div>
            </div>

            <div class="details-card">
              <div class="card-header">
                <h5>Processing Information</h5>
              </div>
              <div class="card-body">
                <div class="detail-row">
                  <strong>Initiated By:</strong> {{ selectedTransfer.initiatedBy }}
                  <span v-if="selectedTransfer.initiatorRole" class="role-text">
                    ({{ selectedTransfer.initiatorRole }})
                  </span>
                </div>

                <div v-if="selectedTransfer.sourceApprovedBy && ['SourceApproved','Approved','Received'].includes(selectedTransfer.status)" class="detail-row">
                  <strong>Source Approved By (ACO):</strong> {{ selectedTransfer.sourceApprovedBy }}
                  <div v-if="selectedTransfer.sourceApprovalDate" class="date-text">
                    on {{ formatDate(selectedTransfer.sourceApprovalDate, true) }}
                  </div>
                </div>

                <div v-if="['Approved','Received'].includes(selectedTransfer.status) && selectedTransfer.approvedBy" class="detail-row">
                  <strong>Destination Approved By (ACO):</strong> {{ selectedTransfer.approvedBy }}
                  <span v-if="selectedTransfer.approverRole" class="role-text">
                    ({{ selectedTransfer.approverRole }})
                  </span>
                  <div v-if="selectedTransfer.approvalDate" class="date-text">
                    on {{ formatDate(selectedTransfer.approvalDate, true) }}
                  </div>
                </div>

                <div v-if="selectedTransfer.status === 'Received'" class="detail-row">
                  <strong>Received By:</strong> {{ selectedTransfer.receivedBy }}
                  <div v-if="selectedTransfer.receivedAt" class="date-text">
                    on {{ formatDate(selectedTransfer.receivedAt, true) }}
                  </div>
                </div>

                <div v-if="selectedTransfer.status === 'Rejected'" class="detail-row">
                  <strong>Rejected By:</strong> {{ selectedTransfer.rejectedBy }}
                  <span v-if="selectedTransfer.rejectorRole" class="role-text">
                    ({{ selectedTransfer.rejectorRole }})
                  </span>
                  <div v-if="selectedTransfer.rejectionDate" class="date-text">
                    on {{ formatDate(selectedTransfer.rejectionDate, true) }}
                  </div>
                  <div v-if="selectedTransfer.rejectionReason" class="rejection-reason">
                    <strong>Rejection Reason:</strong><br>
                    {{ selectedTransfer.rejectionReason }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeDetailsModal">Close</button>
          <button
            v-if="(selectedTransfer.status === 'Pending' && canApproveAsSource(selectedTransfer)) || (selectedTransfer.status === 'SourceApproved' && canApproveAsDestination(selectedTransfer))"
            type="button"
            class="btn btn-success"
            @click="approveTransfer(selectedTransfer)"
          >
            {{ selectedTransfer.status === 'Pending' ? 'Confirm (Source ACO)' : 'Confirm (Destination ACO)' }}
          </button>
          <button
            v-if="selectedTransfer.status === 'Pending' || selectedTransfer.status === 'SourceApproved'"
            type="button"
            class="btn btn-danger"
            @click="rejectTransfer(selectedTransfer)"
          >
            Reject Transfer
          </button>
          <button
            v-if="selectedTransfer.status === 'Approved' && canReceiveTransfer(selectedTransfer)"
            type="button"
            class="btn btn-primary"
            @click="receiveTransfer(selectedTransfer)"
          >
            Receive Employee
          </button>
        </div>
      </div>
    </div>

    <!-- Approve Transfer Modal -->
    <div v-if="showApproveModal && selectedTransfer" class="modal-overlay" @click="closeApproveModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Approve Transfer</h3>
          <button class="modal-close" @click="closeApproveModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="confirm-content">
            <div class="confirm-icon success">✓</div>
            <h4>Confirm Transfer Approval</h4>
            <p class="confirm-text">
              You are about to approve the transfer of <strong>{{ selectedTransfer.employeeName }}</strong>
              from <strong>{{ selectedTransfer.fromDepartmentName }}</strong>
              to <strong>{{ selectedTransfer.toDepartmentName }}</strong>.
            </p>
          </div>

          <div class="alert alert-info">
            <div class="alert-icon">ℹ️</div>
            <div class="alert-text">
              After you approve, HR in the destination department must receive the employee. Only then will the employee's department (and position/salary if any) be updated.
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">Approval Remarks (Optional)</label>
            <textarea v-model="approvalForm.remarks" class="form-textarea" rows="3" placeholder="Add any additional comments"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeApproveModal">Cancel</button>
          <button
            type="button"
            class="btn btn-success"
            @click="confirmApproveTransfer"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing" class="btn-spinner"></span>
            {{ isProcessing ? 'Processing...' : 'Approve Transfer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Reject Transfer Modal -->
    <div v-if="showRejectModal && selectedTransfer" class="modal-overlay" @click="closeRejectModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Reject Transfer</h3>
          <button class="modal-close" @click="closeRejectModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="confirm-content">
            <div class="confirm-icon danger">✗</div>
            <h4>Confirm Transfer Rejection</h4>
            <p class="confirm-text">
              You are about to reject the transfer of <strong>{{ selectedTransfer.employeeName }}</strong>
              from <strong>{{ selectedTransfer.fromDepartmentName }}</strong>
              to <strong>{{ selectedTransfer.toDepartmentName }}</strong>.
            </p>
          </div>

          <div class="form-group">
            <label class="form-label required">Reason for Rejection</label>
            <textarea
              v-model="rejectionForm.reason"
              class="form-textarea"
              rows="3"
              placeholder="Provide reason for rejection"
              required
            ></textarea>
            <div v-if="rejectionError" class="form-error">
              {{ rejectionError }}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeRejectModal">Cancel</button>
          <button
            type="button"
            class="btn btn-danger"
            @click="confirmRejectTransfer"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing" class="btn-spinner"></span>
            {{ isProcessing ? 'Processing...' : 'Reject Transfer' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Receive Transfer Modal -->
    <div v-if="showReceiveModal && selectedTransfer" class="modal-overlay" @click="closeReceiveModal">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Receive Employee</h3>
          <button class="modal-close" @click="closeReceiveModal">&times;</button>
        </div>
        <div class="modal-body">
          <div class="confirm-content">
            <div class="confirm-icon success">📥</div>
            <h4>Confirm Receipt of Transfer</h4>
            <p class="confirm-text">
              You are receiving <strong>{{ selectedTransfer.employeeName }}</strong>
              into your department (<strong>{{ selectedTransfer.toDepartmentName }}</strong>).
              The employee's department will be updated to {{ selectedTransfer.toDepartmentName }}.
            </p>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeReceiveModal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            @click="confirmReceiveTransfer"
            :disabled="isProcessing"
          >
            <span v-if="isProcessing" class="btn-spinner"></span>
            {{ isProcessing ? 'Processing...' : 'Receive Employee' }}
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import { getRight, authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "EmployeeTransfers",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      isSubmitting: false,
      isProcessing: false,
      
      // Modal states
      showNewTransferModal: false,
      showDetailsModal: false,
      showApproveModal: false,
      showRejectModal: false,
      showReceiveModal: false,
      currentUserScope: { type: 'department', department_id: null, department_name: null },
      
      transfers: [],
      summary: {},
      pagination: {
        currentPage: 1,
        totalPages: 1,
        limit: 10,
        totalRecords: 0
      },
      filters: {
        status: '',
        search: '',
        fromDate: '',
        toDate: '',
        departmentId: ''
      },
      departments: [],
      employees: [],
      positions: [],
      employeeSearch: '',
      selectedTransfer: null,
      selectedEmployee: null,

      transferForm: {
        employeeId: '',
        fromDepartmentId: '',
        toDepartmentId: '',
        transferDate: new Date().toISOString().slice(0, 10),
        effectiveDate: new Date().toISOString().slice(0, 10),
        reason: '',
        newPosition: '',
        remarks: ''
      },

      approvalForm: {
        remarks: ''
      },

      rejectionForm: {
        reason: ''
      },
      rejectionError: ''
    };
  },

  computed: {
    today() {
      return new Date().toISOString().slice(0, 10);
    },
    filteredEmployees() {
      if (!this.employeeSearch) return this.employees;
      const s = this.employeeSearch.toLowerCase();
      return this.employees.filter(e => 
        (e.firstName && e.firstName.toLowerCase().includes(s)) ||
        (e.lastName && e.lastName.toLowerCase().includes(s)) ||
        (e.nationalId && e.nationalId.toLowerCase().includes(s)) ||
        (e.employeeNo && e.employeeNo.toLowerCase().includes(s))
      );
    }
  },

  created() {
    this.debouncedSearch = _.debounce(this.filterTransfers, 500);
    this.getTransfers();
    this.getDepartments();
    this.getEmployees();
    this.getPositions();
  },

  methods: {
    getPositions() {
      const data = new FormData();
      data.append("function", "getPositions");
      execute(data, authUrl)
        .then((res) => {
          if (res.data.success) {
            this.positions = res.data.data.positions || [];
          }
        })
        .catch(() => {});
    },
    getRight(type) {
      return false;
    },

    canApproveAsSource(transfer) {
      if (!transfer || transfer.status !== 'Pending') return false;
      const scope = this.currentUserScope;
      if (!scope) return false;
      const perms = (scope.permissions || []).map(p => String(p).toUpperCase());
      const hasSource = perms.includes('TRANSFERS_APPROVE_SOURCE');
      const fromId = String(transfer.fromDepartmentId || '');
      const deptId = String(scope.department_id || '');
      return hasSource && fromId && deptId && fromId === deptId;
    },

    canApproveAsDestination(transfer) {
      if (!transfer || transfer.status !== 'SourceApproved') return false;
      const scope = this.currentUserScope;
      if (!scope) return false;
      const perms = (scope.permissions || []).map(p => String(p).toUpperCase());
      const hasDest = perms.includes('TRANSFERS_APPROVE_DESTINATION');
      const toId = String(transfer.toDepartmentId || '');
      const deptId = String(scope.department_id || '');
      return hasDest && toId && deptId && toId === deptId;
    },

    getTransfers() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getEmployeeTransfers");
      data.append("page", this.pagination.currentPage);
      data.append("limit", this.pagination.limit);

      if (this.filters.status) {
        data.append("status", this.filters.status);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      if (this.filters.fromDate) {
        data.append("fromDate", this.filters.fromDate);
      }

      if (this.filters.toDate) {
        data.append("toDate", this.filters.toDate);
      }

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.transfers = res.data.data.transfers || [];
              this.summary = res.data.data.summary || {};
              this.currentUserScope = res.data.data.currentUserScope || { type: 'department', department_id: null, department_name: null };
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                limit: 10,
                totalRecords: 0
              };
            } else {
              this.showToast("error", res.data.message || "Failed to load employee transfers");
              this.transfers = [];
              this.summary = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching transfers");
            this.transfers = [];
            this.summary = {};
          });
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    loadEmployeeData() {
      if (!this.transferForm.employeeId) {
        this.selectedEmployee = null;
        return;
      }

      const data = new FormData();
      data.append("function", "getEmployeeForTransfer");
      data.append("employeeId", this.transferForm.employeeId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedEmployee = res.data.data.employee || null;

              if (this.selectedEmployee) {
                this.transferForm.fromDepartmentId = this.selectedEmployee.departmentID;
              }
            } else {
              this.showToast("error", res.data.message || "Failed to load employee data");
              this.selectedEmployee = null;
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employee data");
            this.selectedEmployee = null;
          });
    },

    // Modal Methods
    openNewTransferModal() {
      this.transferForm = {
        employeeId: '',
        fromDepartmentId: '',
        toDepartmentId: '',
        transferDate: new Date().toISOString().slice(0, 10),
        effectiveDate: new Date().toISOString().slice(0, 10),
        reason: '',
        newPosition: '',
        remarks: ''
      };
      this.selectedEmployee = null;
      this.showNewTransferModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeNewTransferModal() {
      this.showNewTransferModal = false;
      document.body.style.overflow = '';
    },

    viewTransferDetails(transfer) {
      this.selectedTransfer = transfer;
      this.showDetailsModal = true;
      document.body.style.overflow = 'hidden';
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      document.body.style.overflow = '';
    },

    approveTransfer(transfer) {
      this.selectedTransfer = transfer;
      this.approvalForm.remarks = '';
      
      this.closeDetailsModal();
      setTimeout(() => {
        this.showApproveModal = true;
        document.body.style.overflow = 'hidden';
      }, 100);
    },

    closeApproveModal() {
      this.showApproveModal = false;
      document.body.style.overflow = '';
    },

    rejectTransfer(transfer) {
      this.selectedTransfer = transfer;
      this.rejectionForm.reason = '';
      this.rejectionError = '';
      
      this.closeDetailsModal();
      setTimeout(() => {
        this.showRejectModal = true;
        document.body.style.overflow = 'hidden';
      }, 100);
    },

    closeRejectModal() {
      this.showRejectModal = false;
      document.body.style.overflow = '';
    },

    receiveTransfer(transfer) {
      this.selectedTransfer = transfer;
      this.closeDetailsModal();
      setTimeout(() => {
        this.showReceiveModal = true;
        document.body.style.overflow = 'hidden';
      }, 100);
    },

    closeReceiveModal() {
      this.showReceiveModal = false;
      document.body.style.overflow = '';
    },

    canReceiveTransfer(transfer) {
      if (!transfer || transfer.status !== 'Approved') return false;
      const scope = this.currentUserScope;
      if (!scope) return false;
      if (scope.type === 'hr_admin') return true;
      const toId = String(transfer.toDepartmentId || '');
      const deptId = String(scope.department_id || '');
      return toId !== '' && deptId !== '' && toId === deptId;
    },

    confirmReceiveTransfer() {
      if (!this.selectedTransfer) return;
      this.isProcessing = true;
      const data = new FormData();
      data.append("function", "receiveEmployeeTransfer");
      data.append("transferId", this.selectedTransfer.id);
      execute(data, authUrl)
        .then((res) => {
          this.isProcessing = false;
          if (res.data.success) {
            this.showToast("success", res.data.message || "Employee received. Department updated.");
            this.closeReceiveModal();
            this.refreshData();
          } else {
            this.showToast("error", res.data.message || "Failed to receive transfer");
          }
        })
        .catch((err) => {
          this.isProcessing = false;
          console.error(err);
          this.showToast("error", "Network error when receiving transfer");
        });
    },

    submitTransferForm() {
      if (!this.transferForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return;
      }

      if (!this.transferForm.toDepartmentId) {
        this.showToast("error", "Please select a target department");
        return;
      }

      if (this.transferForm.fromDepartmentId === this.transferForm.toDepartmentId) {
        this.showToast("error", "Target department must be different from current department");
        return;
      }

      if (!this.transferForm.reason) {
        this.showToast("error", "Please provide a reason for the transfer");
        return;
      }

      this.isSubmitting = true;

      const data = new FormData();
      data.append("function", "createEmployeeTransfer");
      data.append("employeeId", this.transferForm.employeeId);
      data.append("fromDepartmentId", this.transferForm.fromDepartmentId);
      data.append("toDepartmentId", this.transferForm.toDepartmentId);
      data.append("transferDate", this.transferForm.transferDate);
      data.append("effectiveDate", this.transferForm.effectiveDate);
      data.append("reason", this.transferForm.reason);

      if (this.transferForm.newPosition) {
        data.append('newPosition', this.transferForm.newPosition);
      }

      if (this.transferForm.remarks) {
        data.append("remarks", this.transferForm.remarks);
      }

      execute(data, authUrl)
          .then((res) => {
            this.isSubmitting = false;

            if (res.data.success) {
              this.showToast("success", res.data.message || "Transfer request created successfully");
              this.closeNewTransferModal();
              this.refreshData();
            } else {
              this.showToast("error", res.data.message || "Failed to create transfer request");
            }
          })
          .catch((error) => {
            this.isSubmitting = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when creating transfer request");
          });
    },

    confirmApproveTransfer() {
      if (!this.selectedTransfer) {
        return;
      }

      this.isProcessing = true;

      const data = new FormData();
      data.append("function", "approveEmployeeTransfer");
      data.append("transferId", this.selectedTransfer.id);

      if (this.approvalForm.remarks) {
        data.append("remarks", this.approvalForm.remarks);
      }

      execute(data, authUrl)
          .then((res) => {
            this.isProcessing = false;

            if (res.data.success) {
              this.showToast("success", res.data.message || "Transfer request approved successfully");
              this.closeApproveModal();
              this.refreshData();
            } else {
              this.showToast("error", res.data.message || "Failed to approve transfer request");
            }
          })
          .catch((error) => {
            this.isProcessing = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when approving transfer request");
          });
    },

    confirmRejectTransfer() {
      if (!this.selectedTransfer) {
        return;
      }

      if (!this.rejectionForm.reason.trim()) {
        this.rejectionError = "Rejection reason is required";
        return;
      }

      this.isProcessing = true;
      this.rejectionError = '';

      const data = new FormData();
      data.append("function", "rejectEmployeeTransfer");
      data.append("transferId", this.selectedTransfer.id);
      data.append("reason", this.rejectionForm.reason);

      execute(data, authUrl)
          .then((res) => {
            this.isProcessing = false;

            if (res.data.success) {
              this.showToast("success", res.data.message || "Transfer request rejected successfully");
              this.closeRejectModal();
              this.refreshData();
            } else {
              this.rejectionError = res.data.message || "Failed to reject transfer request";
            }
          })
          .catch((error) => {
            this.isProcessing = false;
            console.error("API error:", error);
            this.rejectionError = "Network error when rejecting transfer request";
          });
    },

    // Helper functions
    refreshData() {
      this.getTransfers();
    },

    resetFilters() {
      this.filters = {
        status: '',
        search: '',
        fromDate: '',
        toDate: '',
        departmentId: ''
      };
      this.pagination.currentPage = 1;
      this.getTransfers();
    },

    filterTransfers() {
      this.pagination.currentPage = 1;
      this.getTransfers();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.pagination.currentPage) {
        return;
      }

      this.pagination.currentPage = page;
      this.getTransfers();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 0;
      const currentPage = this.pagination.currentPage;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    getRowClass(status) {
      switch (status) {
        case 'SourceApproved': return 'row-source-approved';
        case 'Approved': return 'row-info';
        case 'Received': return 'row-success';
        case 'Rejected': return 'row-danger';
        default: return '';
      }
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'Pending': return 'status-warning';
        case 'SourceApproved': return 'status-source-approved';
        case 'Approved': return 'status-info';
        case 'Received': return 'status-success';
        case 'Rejected': return 'status-danger';
        default: return 'status-default';
      }
    },

    getInitials(name) {
      if (!name) return '';
      return name.split(' ').map(part => part.charAt(0)).join('').toUpperCase();
    },

    formatDate(dateString, includeTime = false) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      if (isNaN(date)) return 'Invalid Date';

      const options = includeTime
          ? { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
          : { year: 'numeric', month: 'short', day: 'numeric' };

      return date.toLocaleDateString('en-US', options);
    },

    formatCurrency(value, currency) {
      if (!value && value !== 0) return 'N/A';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 2
      }).format(value);
    },

    showToast(type, message) {
      if (this.$toast) {
        this.$toast[type](message);
      } else {
        if (type === 'error') {
          alert('Error: ' + message);
        } else {
          alert(message);
        }
      }
    }
  }
};
</script>

<style scoped>
/* Layout */
.employee-transfers {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.layout-content {
  padding: 20px;
}

.page-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
}

.breadcrumb-item {
  color: #666;
}

.breadcrumb-current {
  color: #333;
  font-weight: 500;
}

.breadcrumb-separator {
  color: #999;
}

/* Stats Cards */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-left: 4px solid;
}

.stat-primary {
  border-color: #0066cc;
}

.stat-warning {
  border-color: #ffc107;
}

.stat-success {
  border-color: #00a854;
}

.stat-info {
  border-color: #17a2b8;
}

.stat-danger {
  border-color: #dc3545;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.stat-primary .stat-icon {
  background-color: rgba(0, 102, 204, 0.1);
  color: #0066cc;
}

.stat-warning .stat-icon {
  background-color: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.stat-success .stat-icon {
  background-color: rgba(0, 168, 84, 0.1);
  color: #00a854;
}

.stat-info .stat-icon {
  background-color: rgba(23, 162, 184, 0.1);
  color: #17a2b8;
}

.stat-danger .stat-icon {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.stat-content {
  flex: 1;
}

.stat-title {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.stat-subtext {
  font-size: 12px;
  color: #666;
}

.stat-subtext.warning {
  color: #ffc107;
}

.stat-subtext.success {
  color: #00a854;
}

.stat-subtext.danger {
  color: #dc3545;
}

/* Filters Card */
.filters-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 24px;
}

.filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.filters-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.filters-actions {
  display: flex;
  gap: 8px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.filter-group {
  display: flex;
  flex-direction: column;
}

.filter-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 6px;
}

.filter-select,
.filter-input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  background: white;
}

.filter-select:focus,
.filter-input:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

.search-input-container {
  position: relative;
}

.search-input {
  padding-right: 36px;
  width: 100%;
}

.search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-icon {
  padding: 8px 12px;
}

.btn-icon .icon {
  font-size: 16px;
}

.btn-primary {
  background-color: #0066cc;
  color: white;
  border-color: #0066cc;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0052a3;
}

.btn-success {
  background-color: #00a854;
  color: white;
  border-color: #00a854;
}

.btn-success:hover:not(:disabled) {
  background-color: #008f46;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-info {
  background-color: #0dcaf0;
  color: white;
  border-color: #0dcaf0;
}

.btn-info:hover:not(:disabled) {
  background-color: #0bb6d9;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
  border-color: #ddd;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.btn-outline {
  background-color: transparent;
  color: #0066cc;
  border-color: #0066cc;
}

.btn-outline:hover:not(:disabled) {
  background-color: rgba(0, 102, 204, 0.1);
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-right: 6px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Transfers Table Card */
.transfers-table-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 102, 204, 0.1);
  border-top-color: #0066cc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-container p {
  margin-top: 12px;
  color: #666;
}

/* Empty Container */
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-container h4 {
  margin: 0 0 8px 0;
  color: #333;
}

.empty-text {
  color: #666;
  margin-bottom: 16px;
  max-width: 400px;
}

/* Table */
.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 14px;
  white-space: nowrap;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  color: #333;
  vertical-align: middle;
}

.data-table tbody tr {
  transition: background-color 0.2s;
}

.data-table tbody tr:hover {
  background-color: rgba(0, 102, 204, 0.05);
}

.row-success {
  background-color: rgba(25, 135, 84, 0.05);
}

.row-info {
  background-color: rgba(23, 162, 184, 0.05);
}

.row-source-approved {
  background-color: rgba(111, 66, 193, 0.08);
}

.row-danger {
  background-color: rgba(220, 53, 69, 0.05);
}

.text-center {
  text-align: center;
}

/* Employee Cell */
.employee-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.employee-avatar {
  width: 32px;
  height: 32px;
  background-color: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #333;
  flex-shrink: 0;
}

.employee-info {
  display: flex;
  flex-direction: column;
}

.employee-name {
  font-weight: 500;
  color: #333;
}

.employee-id {
  font-size: 12px;
  color: #666;
}

/* Date Cell */
.date-cell {
  display: flex;
  flex-direction: column;
}

.effective-date {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

/* Status Badge */
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
}

.status-warning {
  background-color: #ffc107;
}

.status-info {
  background-color: #17a2b8;
  color: #fff;
}

.status-source-approved {
  background-color: #6f42c1;
  color: #fff;
}

.status-success {
  background-color: #00a854;
}

.status-danger {
  background-color: #dc3545;
}

.status-default {
  background-color: #6c757d;
}

/* Initiator Cell */
.initiator-cell {
  display: flex;
  flex-direction: column;
}

.initiator-date {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

/* Actions Cell */
.actions-cell {
  white-space: nowrap;
}

.actions-cell .btn {
  margin: 0 2px;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid #e9ecef;
}

.pagination-info {
  font-size: 14px;
  color: #666;
}

.info-bold {
  font-weight: 600;
  color: #333;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn:not(:disabled):hover {
  background-color: #f0f0f0;
}

.pagination-pages {
  display: flex;
  gap: 4px;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn.active {
  background-color: #0066cc;
  color: white;
  border-color: #0066cc;
}

.page-btn.disabled {
  border: none;
  background: transparent;
  cursor: default;
}

.page-btn:not(.active):not(.disabled):hover {
  background-color: #f0f0f0;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.2s ease-out;
}

.modal-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  width: 500px;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
}

.modal-container.large {
  width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #666;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.modal-close:hover {
  background-color: #f0f0f0;
  color: #333;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Form Styles */
.form-group {
  margin-bottom: 16px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
  background-color: white;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

.form-textarea {
  min-height: 60px;
  resize: vertical;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.form-error {
  font-size: 12px;
  color: #dc3545;
  margin-top: 4px;
}

.input-with-button {
  display: flex;
  gap: 8px;
}

.input-with-button .form-input {
  flex: 1;
}

/* Employee Details Card */
.employee-details-card {
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
  border: 1px solid #e9ecef;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.details-column h4 {
  margin: 0 0 8px 0;
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.detail-item {
  margin-bottom: 4px;
  font-size: 14px;
  color: #333;
}

/* Transfer Details */
.transfer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left h4 {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.transfer-id {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.header-right {
  text-align: right;
  font-size: 14px;
  color: #333;
}

.transfer-date,
.effective-date,
.request-date {
  margin-bottom: 4px;
}

.transfer-direction {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  gap: 24px;
}

.department-box {
  flex: 1;
  text-align: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.department-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.department-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.direction-arrow {
  font-size: 24px;
  color: #666;
}

/* Details Cards */
.details-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.card-header {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.card-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.card-body {
  padding: 16px;
}

.detail-row {
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.role-text {
  font-size: 12px;
  color: #666;
  margin-left: 4px;
}

.date-text {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.rejection-reason {
  margin-top: 12px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
}

/* Confirm Content */
.confirm-content {
  text-align: center;
  margin-bottom: 24px;
}

.confirm-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin: 0 auto 16px;
}

.confirm-icon.success {
  background-color: rgba(0, 168, 84, 0.1);
  color: #00a854;
}

.confirm-icon.danger {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.confirm-content h4 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.confirm-text {
  color: #666;
  line-height: 1.5;
}

.confirm-text strong {
  color: #333;
}

/* Alert */
.alert {
  display: flex;
  padding: 12px;
  background-color: #e7f3ff;
  border-radius: 6px;
  margin-bottom: 16px;
  gap: 8px;
}

.alert-info {
  background-color: #e7f3ff;
  border-left: 4px solid #0066cc;
}

.alert-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.alert-text {
  font-size: 14px;
  color: #333;
  line-height: 1.5;
}

/* Salary Blur */
.salary-blur {
  filter: blur(5px);
  color: transparent;
  user-select: none;
  transition: filter 0.3s ease;
}

.salary-blur:hover {
  filter: blur(3px);
}

.cursor-pointer {
  cursor: pointer;
}

/* Responsive Design */
@media (max-width: 768px) {
  .layout-content {
    padding: 12px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .filters-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .filters-grid {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
  }
  
  .transfer-header {
    flex-direction: column;
  }
  
  .header-right {
    text-align: left;
  }
  
  .transfer-direction {
    flex-direction: column;
    gap: 12px;
  }
  
  .direction-arrow {
    transform: rotate(90deg);
  }
  
  .pagination-container {
    flex-direction: column;
    gap: 12px;
  }
  
  .modal-container.large {
    width: 100%;
    max-height: 95vh;
  }
  
  .modal-body {
    padding: 16px;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 12px;
  }
  
  .actions-cell {
    display: flex;
    gap: 4px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 20px;
  }
  
  .stat-card {
    padding: 12px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .modal-footer .btn {
    width: 100%;
  }
}
</style>